/**
 * Hook for managing market data with automatic caching via Zustand store
 * Integrates InfoClient with the centralized market store
 */

import { useCallback, useEffect, useRef } from 'react';
import { InfoClient } from '../sdk';
import { useMarketStore } from '../store';
import type { Market, OrderbookData, TickerData, RecentTrade } from '../store/types';

export interface UseMarketsConfig {
  baseURL: string;
  sessionToken?: string;
  autoFetchMarkets?: boolean; // Auto-fetch markets list on mount
}

/**
 * Hook for centralized market data management
 * 
 * @example
 * ```tsx
 * const { markets, getMarket, fetchMarkets, fetchOrderbook } = useMarkets({
 *   baseURL: 'https://router.morpheum.io',
 *   autoFetchMarkets: true,
 * });
 * ```
 */
export function useMarkets(config: UseMarketsConfig) {
  const infoClientRef = useRef<InfoClient | null>(null);
  
  // Store state and actions
  const markets = useMarketStore(state => state.markets);
  const marketsById = useMarketStore(state => state.marketsById);
  const orderbooks = useMarketStore(state => state.orderbooks);
  const tickers = useMarketStore(state => state.tickers);
  const recentTrades = useMarketStore(state => state.recentTrades);
  const selectedMarketId = useMarketStore(state => state.selectedMarketId);
  const loadingMarkets = useMarketStore(state => state.loadingMarkets);
  const loadingOrderbooks = useMarketStore(state => state.loadingOrderbooks);
  const loadingTickers = useMarketStore(state => state.loadingTickers);
  const loadingTrades = useMarketStore(state => state.loadingTrades);
  const errors = useMarketStore(state => state.errors);
  
  const setMarkets = useMarketStore(state => state.setMarkets);
  const setMarket = useMarketStore(state => state.setMarket);
  const getMarket = useMarketStore(state => state.getMarket);
  const getMarketBySymbol = useMarketStore(state => state.getMarketBySymbol);
  const setSelectedMarketId = useMarketStore(state => state.setSelectedMarketId);
  const setOrderbook = useMarketStore(state => state.setOrderbook);
  const getOrderbook = useMarketStore(state => state.getOrderbook);
  const updateOrderbook = useMarketStore(state => state.updateOrderbook);
  const setTicker = useMarketStore(state => state.setTicker);
  const getTicker = useMarketStore(state => state.getTicker);
  const setRecentTrades = useMarketStore(state => state.setRecentTrades);
  const getRecentTrades = useMarketStore(state => state.getRecentTrades);
  const setLoading = useMarketStore(state => state.setLoading);
  const setError = useMarketStore(state => state.setError);
  const clearError = useMarketStore(state => state.clearError);
  const clearCache = useMarketStore(state => state.clearCache);
  
  // Initialize InfoClient
  useEffect(() => {
    if (!infoClientRef.current) {
      infoClientRef.current = new InfoClient({
        baseURL: config.baseURL,
        sessionToken: config.sessionToken,
      });
    } else if (config.sessionToken) {
      infoClientRef.current.setSessionToken(config.sessionToken);
    }
  }, [config.baseURL, config.sessionToken]);
  
  // Fetch all markets
  const fetchMarkets = useCallback(async (): Promise<Market[]> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    const key = 'markets';
    setLoading(key, true);
    setError(key, null);
    
    try {
      const data = await infoClientRef.current.getMarkets();
      const rawMarkets = Array.isArray(data) ? data : (data?.markets || []);
      
      // Normalize API response to Market interface
      // API returns: market_id (string), status ("active"), moniker, base_asset, quote_asset
      // Market interface expects: id (number), active (boolean), symbol, moniker, baseAsset, quoteAsset
      const marketsList: Market[] = rawMarkets.map((m: any, index: number) => {
        // Convert market_id (string) to id (number)
        // Handle large string IDs that exceed JavaScript's safe integer limit (2^53 - 1)
        let id: number;
        if (m.id !== undefined) {
          id = typeof m.id === 'string' ? parseInt(m.id, 10) || (index + 1) : m.id;
        } else if (m.market_id !== undefined) {
          // Try to parse the market_id string to number
          // For very large IDs, use modulo or last safe digits to fit in JavaScript number
          const marketIdStr = String(m.market_id);
          const parsed = parseInt(marketIdStr, 10);
          if (isNaN(parsed)) {
            // If parsing fails, use index-based ID
            id = index + 1;
          } else if (parsed > Number.MAX_SAFE_INTEGER) {
            // If too large, use modulo to get a safe integer
            // This ensures we get a unique but safe number
            id = parsed % Number.MAX_SAFE_INTEGER || (index + 1);
          } else {
            id = parsed;
          }
        } else {
          // Fallback to index-based ID
          id = index + 1;
        }
        
        return {
          id,
          symbol: m.symbol || m.moniker, // Use moniker as symbol if symbol not available
          moniker: m.moniker,
          baseAsset: m.base_asset || m.baseAsset,
          quoteAsset: m.quote_asset || m.quoteAsset,
          active: m.active !== undefined ? m.active : (m.status === 'active'),
          // Preserve original fields for reference
          market_id: m.market_id,
          status: m.status,
          ...m, // Spread to preserve any other fields
        };
      });
      
      setMarkets(marketsList);
      return marketsList;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch markets';
      setError(key, errorMessage);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }, [setMarkets, setLoading, setError]);
  
  // Fetch single market by ID
  const fetchMarket = useCallback(async (marketId: string | number): Promise<Market | null> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    // Check cache first - convert string marketId to number for internal lookup
    const marketIdNum = typeof marketId === 'string' ? parseInt(marketId, 10) : marketId;
    const cached = !isNaN(marketIdNum) ? getMarket(marketIdNum) : null;
    if (cached) {
      return cached;
    }
    
    try {
      const market = await infoClientRef.current.getMarketByID(marketId);
      if (market) {
        setMarket(market);
        return market;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch market ${marketId}`;
      setError(`market_${marketId}`, errorMessage);
      throw error;
    }
  }, [getMarket, setMarket, setError]);
  
  // Fetch market by symbol/moniker
  const fetchMarketBySymbol = useCallback(async (symbol: string): Promise<Market | null> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    // Check cache first
    const cached = getMarketBySymbol(symbol);
    if (cached) {
      return cached;
    }
    
    try {
      const market = await infoClientRef.current.getMarketByMoniker(symbol);
      if (market) {
        setMarket(market);
        return market;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch market ${symbol}`;
      setError(`market_${symbol}`, errorMessage);
      throw error;
    }
  }, [getMarketBySymbol, setMarket, setError]);
  
  // Fetch orderbook
  const fetchOrderbook = useCallback(async (
    marketId: string | number,
    convertToDecimal: boolean = true,
    depth: number = 20
  ): Promise<OrderbookData | null> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    const key = `orderbook_${marketId}`;
    setLoading(key, true);
    setError(key, null);
    
    try {
      const data = await infoClientRef.current.getOrderbook(marketId, convertToDecimal, depth);
      if (data && (data.bids || data.asks)) {
        const orderbook: OrderbookData = {
          bids: data.bids || [],
          asks: data.asks || [],
          timestamp: Date.now(),
        };
        setOrderbook(typeof marketId === 'string' ? marketId : marketId.toString(), orderbook);
        return orderbook;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch orderbook for market ${marketId}`;
      setError(key, errorMessage);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }, [setOrderbook, setLoading, setError]);
  
  // Fetch ticker
  const fetchTicker = useCallback(async (marketId: string | number): Promise<TickerData | null> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    const key = `ticker_${marketId}`;
    setLoading(key, true);
    setError(key, null);
    
    try {
      const data = await infoClientRef.current.getTicker(marketId);
      if (data) {
        const marketIdStr = typeof marketId === 'string' ? marketId : marketId.toString();
        const ticker: TickerData = {
          marketId: marketIdStr,
          ...data,
          timestamp: Date.now(),
        };
        setTicker(marketIdStr, ticker);
        return ticker;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch ticker for market ${marketId}`;
      setError(key, errorMessage);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }, [setTicker, setLoading, setError]);
  
  // Fetch recent trades
  const fetchRecentTrades = useCallback(async (
    marketId: string | number,
    limit: number = 100
  ): Promise<RecentTrade[] | null> => {
    if (!infoClientRef.current) {
      throw new Error('InfoClient not initialized');
    }
    
    const key = `trades_${marketId}`;
    setLoading(key, true);
    setError(key, null);
    
    try {
      const data = await infoClientRef.current.getRecentTrades(marketId, limit);
      if (data) {
        const trades: RecentTrade[] = Array.isArray(data) ? data : [];
        setRecentTrades(typeof marketId === 'string' ? marketId : marketId.toString(), trades);
        return trades;
      }
      return null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to fetch trades for market ${marketId}`;
      setError(key, errorMessage);
      throw error;
    } finally {
      setLoading(key, false);
    }
  }, [setRecentTrades, setLoading, setError]);
  
  // Auto-fetch markets on mount if enabled
  useEffect(() => {
    if (config.autoFetchMarkets && markets.length === 0 && !loadingMarkets) {
      fetchMarkets().catch(console.error);
    }
  }, [config.autoFetchMarkets, markets.length, loadingMarkets, fetchMarkets]);
  
  // Find selected market and derive symbol
  const selectedMarket = selectedMarketId
    ? markets.find(
        (m) =>
          m.market_id === selectedMarketId ||
          m.id?.toString() === selectedMarketId
      )
    : null;

  // Derive market symbol: moniker > symbol > baseAsset/quoteAsset > null
  const selectedMarketSymbol = selectedMarket
    ? selectedMarket.moniker ||
      selectedMarket.symbol ||
      (selectedMarket.baseAsset && selectedMarket.quoteAsset
        ? `${selectedMarket.baseAsset}-${selectedMarket.quoteAsset}`
        : null)
    : null;

  // Get symbol by market ID (from cache)
  const getSymbolByMarketId = useCallback((marketId: string | number): string | null => {
    const marketIdStr = typeof marketId === 'string' ? marketId : marketId.toString();
    
    // Try to find market by market_id (string) or id (number)
    const market = markets.find(
      (m) =>
        m.market_id === marketIdStr ||
        m.id?.toString() === marketIdStr
    );

    if (!market) {
      return null;
    }

    // Return symbol in priority: moniker > symbol > baseAsset/quoteAsset
    return (
      market.moniker ||
      market.symbol ||
      (market.baseAsset && market.quoteAsset
        ? `${market.baseAsset}/${market.quoteAsset}`
        : null)
    );
  }, [markets]);
  
  return {
    // Data
    markets,
    marketsById,
    orderbooks,
    tickers,
    recentTrades,
    selectedMarketId,
    selectedMarket,
    selectedMarketSymbol,
    
    // Loading states
    loadingMarkets,
    loadingOrderbooks,
    loadingTickers,
    loadingTrades,
    isLoading: (key: string) => {
      if (key === 'markets') return loadingMarkets;
      if (key.startsWith('orderbook_')) {
        const marketId = key.split('_').slice(1).join('_'); // Get everything after 'orderbook_'
        return loadingOrderbooks.has(marketId);
      }
      if (key.startsWith('ticker_')) {
        const marketId = key.split('_').slice(1).join('_'); // Get everything after 'ticker_'
        return loadingTickers.has(marketId);
      }
      if (key.startsWith('trades_')) {
        const marketId = key.split('_').slice(1).join('_'); // Get everything after 'trades_'
        return loadingTrades.has(marketId);
      }
      return false;
    },
    
    // Errors
    errors,
    getError: (key: string) => errors.get(key),
    
    // Getters (from cache)
    getMarket,
    getMarketBySymbol,
    getSymbolByMarketId,
    getOrderbook,
    getTicker,
    getRecentTrades,
    
    // Fetchers (from API)
    fetchMarkets,
    fetchMarket,
    fetchMarketBySymbol,
    fetchOrderbook,
    fetchTicker,
    fetchRecentTrades,
    
    // Store actions
    updateOrderbook,
    clearCache,
    clearError,
    setSelectedMarketId,
    
    // Direct access to InfoClient if needed
    infoClient: infoClientRef.current,
  };
}
